import { type PublicClip } from '@air/api';
import { type Board, type Clip, type PublicBoard } from '@air/api/types';
import { type SelectableItem } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { type FacePerson } from '~/swr-hooks/person/types';

export type BaseSelectableGalleryItem<Type extends GalleryItemType> = {
  type: Type;
} & SelectableItem;

export type SelectableGalleryAssetItem = {
  item: Clip | PublicClip;
} & BaseSelectableGalleryItem<GalleryItemType.asset>;

export type SelectableGalleryFileItem = {
  item: Clip | PublicClip;
} & BaseSelectableGalleryItem<GalleryItemType.file>;

export type SelectableGalleryBoardItem<B> = {
  item: B;
} & BaseSelectableGalleryItem<GalleryItemType.board>;

export type SelectableGalleryTopLevelBoardItem<B> = {
  item: B;
} & BaseSelectableGalleryItem<GalleryItemType.topLevelBoard>;

export type SelectableGalleryPersonItem = {
  item: FacePerson;
} & BaseSelectableGalleryItem<GalleryItemType.person>;

export type SelectableGalleryCanvasItem = {
  item: CanvasListItem;
} & BaseSelectableGalleryItem<GalleryItemType.canvas>;

export type SelectableGalleryItem =
  | SelectableGalleryAssetItem
  | SelectableGalleryBoardItem<Board | PublicBoard>
  | SelectableGalleryTopLevelBoardItem<Board | PublicBoard>
  | SelectableGalleryFileItem
  | SelectableGalleryPersonItem
  | SelectableGalleryCanvasItem;

export const isSelectableGalleryItem = (item: SelectableGalleryItem | SelectableItem): item is SelectableGalleryItem =>
  item.type === GalleryItemType.asset ||
  item.type === GalleryItemType.board ||
  item.type === GalleryItemType.topLevelBoard ||
  item.type === GalleryItemType.file ||
  item.type === GalleryItemType.person ||
  item.type === GalleryItemType.canvas;

export type ClipAndAssetId = Pick<Clip, 'id' | 'assetId'>;
